/**
 * Web Component: Nosto Prefetch
 * -----------------------------------------------------------------------------
 * Standalone web component that prefetches Nosto collection data before Vue
 * loads. This improves collection page performance by starting the API query
 * as early as possible.
 */

class NostoPrefetch extends HTMLElement {
  /**
   * Creates an instance of NostoPrefetch.
   * Initializes default values for collection ID and products per page limit.
   */
  constructor() {
    super()
    this.collectionId = null
    this.limit = 16
    this.cacheDurationMinutes = 60
  }

  /**
   * Called when the element is added to the DOM.
   * Reads attributes and initiates the prefetch process if conditions are met.
   */
  connectedCallback() {
    if (window.cnvsPrefetchStarted) {
      return
    }

    this.collectionId = this.getAttribute('collection-id')
    this.limit = parseInt(this.getAttribute('limit') || '16', 10)
    this.cacheDurationMinutes = parseInt(this.getAttribute('cache-duration') || '60', 10)

    const cachedData = this.getCachedData()

    if (cachedData) {
      this.useCachedData(cachedData)
      return
    }

    if (this.shouldSkipPrefetch()) {
      return
    }

    this.initPrefetch()
  }

  /**
   * Determines if the prefetch should be skipped.
   * Skips prefetch when URL contains filters, sorting, or pagination parameters,
   * or when no collection ID is provided.
   * @returns {Boolean} - True if prefetch should be skipped.
   */
  shouldSkipPrefetch() {
    const urlParams = window.location.search

    if (urlParams && urlParams.length > 1) {
      if (urlParams.includes('pf_') || urlParams.includes('sort=') || urlParams.includes('page=')) {
        return true
      }
    }

    return !this.collectionId
  }

  /**
   * Initializes the prefetch process.
   * Sets up the global prefetch state and waits for Nosto JS to be available.
   */
  initPrefetch() {
    window.cnvsPrefetchStarted = true
    performance.mark('cnvs.prefetch.start')

    window.cnvsPrefetchedNosto = {
      status: 'loading',
      data: null,
      collectionId: this.collectionId,
    }

    this.waitForNosto(() => this.getSessionAndQuery())
  }

  /**
   * Waits for the Nosto JS library to be available on the window object.
   * Uses polling with a timeout to avoid blocking indefinitely.
   * @param {Function} callback - Function to call when Nosto JS is ready.
   */
  waitForNosto(callback) {
    const timeout = 10000
    const pollRate = 50
    const startTime = performance.now()

    const intervalId = setInterval(() => {
      if (window.nostojs) {
        clearInterval(intervalId)
        performance.mark('cnvs.prefetch.nosto-ready')
        callback()
        return
      }

      if (performance.now() - startTime >= timeout) {
        clearInterval(intervalId)
        window.cnvsPrefetchedNosto.status = 'error'
      }
    }, pollRate)
  }

  /**
   * Retrieves the Nosto session and initiates the query.
   * Checks for an existing session in cookies before making a new request.
   */
  getSessionAndQuery() {
    const existingSession = this.getSessionFromCookie()

    if (existingSession) {
      this.makeNostoQuery(existingSession)
      return
    }

    window.nostojs((api) => {
      api.getSearchSessionParams()
        .then((response) => {
          const encodedSession = encodeURIComponent(JSON.stringify(response))
          document.cookie = `nostoSearchSessionParams=${encodedSession}`
          this.makeNostoQuery(encodedSession)

        }).catch(() => {
          window.cnvsPrefetchedNosto.status = 'error'
        })
    })
  }

  /**
   * Retrieves the Nosto session from browser cookies.
   * @returns {String|null} - The session cookie value or null if not found.
   */
  getSessionFromCookie() {
    const cookieValue = `; ${document.cookie}`
    const parts = cookieValue.split('; nostoSearchSessionParams=')

    if (parts.length === 2) {
      return parts.pop().split(';').shift()
    }

    return null
  }

  /**
   * Makes the Nosto API search query with the provided session cookie.
   * Stores the response in window.cnvsPrefetchedNosto for Vue to consume.
   * @param {String} sessionCookie - The encoded session parameters.
   */
  makeNostoQuery(sessionCookie) {
    const productData = {
      size: this.limit,
      from: 0,
      facets: ['*'],
      fields: [
        'available',
        'priceCurrencyCode',
        'listPrice',
        'price',
        'productId',
        'url',
        'name',
        'tags1',
        'tags2',
        'tags3',
        'alternateImageUrls',
        'description',
        'imageUrl',
        'brand',
        'customFields.key',
        'customFields.value',
        'skus.id',
        'skus.name',
        'skus.listPrice',
        'skus.price',
        'skus.availability',
        'skus.customFields.key',
        'skus.customFields.value',
      ],
      categoryId: this.collectionId,
    }

    const searchParams = {
      products: productData,
      keywords: {
        fields: ['keyword'],
        size: 5,
      },
      sessionParams: JSON.parse(decodeURIComponent(sessionCookie)),
    }

    performance.mark('cnvs.prefetch.query-start')

    window.nostojs((api) => {
      api.search(searchParams, {
        redirect: false,
        track: false,
      })
        .then((response) => {
          performance.mark('cnvs.prefetch.complete')
          performance.measure('cnvs.prefetch', 'cnvs.prefetch.start', 'cnvs.prefetch.complete')
          performance.measure('cnvs.prefetch.query', 'cnvs.prefetch.query-start', 'cnvs.prefetch.complete')

          const timestamp = Date.now()

          window.cnvsPrefetchedNosto = {
            status: 'complete',
            data: response,
            collectionId: this.collectionId,
            timestamp,
          }

          // Cache the response for future page loads
          this.setCachedData(response, timestamp)

  /**
    * Fire the Nosto category-tracking pixel after the product grid has
    * rendered. Executed strictly after `cnvs.nosto.render.end` is marked
    * to prevent `wpm:pixel` events from blocking the main thread before
    * Vue finishes painting.
    * @inner
    * @returns {void}
    */
          const fireTracking = () => {
            const sessionCookie = this.getSessionFromCookie()

            if (!sessionCookie) {
              return
            }

            window.nostojs((trackApi) => {
              trackApi.search({
                products: {
                  size: 0,
                  categoryId: this.collectionId,
                  fields: ['productId'],
                  facets: [],
                },
                keywords: { fields: ['keyword'], size: 0 },
                sessionParams: JSON.parse(decodeURIComponent(sessionCookie)),
              }, {
                redirect: false,
                track: 'category',
              })
            })
          }

          const waitForRender = () => {
            const rendered = performance.getEntriesByName('cnvs.nosto.render.end', 'mark').length > 0

            if (rendered) {
              if ('requestIdleCallback' in window) {
                requestIdleCallback(fireTracking)
              } else {
                setTimeout(fireTracking, 0)
              }
            } else {
              setTimeout(waitForRender, 100)
            }
          }

          setTimeout(waitForRender, 100)

        }).catch(() => {
          window.cnvsPrefetchedNosto.status = 'error'
        })
    })
  }

  /**
   * Cache the Nosto response to sessionStorage.
   * @param {Object} response - Nosto API response.
   * @param {Number} timestamp - Cache timestamp.
   */
  setCachedData(response, timestamp) {
    if (!this.isCachingEnabled()) {
      return
    }

    const cacheKey = this.getCacheKey()

    try {
      const cacheData = {
        timestamp,
        collectionId: String(this.collectionId),
        query: '',
        data: response,
        formattedProducts: null,
        page: 1,
      }

      sessionStorage.setItem(cacheKey, JSON.stringify(cacheData))

    } catch (error) {
      // Handle quota exceeded - clean up old cache entries
      this.cleanupOldCache()

      try {
        const cacheData = {
          timestamp,
          collectionId: String(this.collectionId),
          query: '',
          data: response,
          formattedProducts: null,
          page: 1,
        }

        sessionStorage.setItem(cacheKey, JSON.stringify(cacheData))
      } catch {
        window.console.warn('Failed to cache Nosto response in prefetch', error)
      }
    }
  }

  /**
   * Check if caching is enabled.
   * @returns {Boolean}
   */
  isCachingEnabled() {
    return this.cacheDurationMinutes > 0
  }

  /**
   * Clean up expired cache entries.
   */
  cleanupOldCache() {
    const keysToRemove = []

    Object.keys(sessionStorage).forEach((key) => {
      if (key && key.startsWith('cache-to-')) {
        try {
          const cached = JSON.parse(sessionStorage.getItem(key))

          if (Date.now() - cached.timestamp > this.getCacheDurationMs()) {
            keysToRemove.push(key)
          }
        } catch {
          keysToRemove.push(key)
        }
      }
    })

    keysToRemove.forEach((key) => sessionStorage.removeItem(key))
  }

  /**
   * Generates the cache key for this collection.
   * @returns {String} - The cache key.
   */
  getCacheKey() {
    return `cache-to-collection-${this.collectionId}`
  }

  /**
   * Gets the cache duration in milliseconds.
   * @returns {Number} - Cache duration in ms.
   */
  getCacheDurationMs() {
    return this.cacheDurationMinutes * 60 * 1000
  }

  /**
   * Retrieves cached data from sessionStorage if valid.
   * @returns {Object|null} - Cached data or null if not found/expired.
   */
  getCachedData() {
    const cacheKey = this.getCacheKey()

    try {
      const cached = sessionStorage.getItem(cacheKey)

      if (!cached) {
        return null
      }

      const parsedCache = JSON.parse(cached)

      // Check if cache is expired
      if (Date.now() - parsedCache.timestamp > this.getCacheDurationMs()) {
        sessionStorage.removeItem(cacheKey)
        return null
      }

      // Verify collection ID matches
      if (parsedCache.collectionId !== this.collectionId) {
        return null
      }

      return parsedCache

    } catch {
      return null
    }
  }

  /**
   * Uses cached data instead of making an API call.
   * @param {Object} cachedData - The cached response data.
   */
  useCachedData(cachedData) {
    window.cnvsPrefetchStarted = true

    performance.mark('cnvs.prefetch.cache-hit')

    window.cnvsPrefetchedNosto = {
      status: 'complete',
      data: cachedData.data,
      collectionId: this.collectionId,
      timestamp: cachedData.timestamp,
      fromCache: true,
    }
  }
}

if (!customElements.get('nosto-prefetch')) {
  customElements.define('nosto-prefetch', NostoPrefetch)
}
